import React, { useEffect, useMemo, useState } from 'react';
import sanitizer from '@helpers/sanitizer';
import useBreadcrumbsQuery from '@hooks/product/use-breadcrumbs-query';
import { useContextSelector } from 'use-context-selector';
import { CatalogContext } from '@context/catalog.context';
import useFormatPrice from '@hooks/use-format-price-hook';
import { usePreferences } from '@context/preferences.context';

const CatalogSchema: React.FC = () => {
  const { breadcrumbs } = useBreadcrumbsQuery();
  const { convertFromCents } = useFormatPrice();
  const { currency } = usePreferences();
  const queryResults = useContextSelector(CatalogContext, (v) => v.queryResult);
  const queryResultFacets = useMemo(() => queryResults?.facets?.fields ?? {}, [queryResults]);
  const priceBound = useContextSelector(CatalogContext, (v) => v.priceBound);

  const createCatalogSchema = () => {
    const schemaName = breadcrumbs?.[breadcrumbs.length - 1]?.localizedName;
    const schemaBrand = queryResultFacets?.brand?.length === 1 ? queryResultFacets.brand[0].name : '';

    return {
      '@context': 'http://schema.org',
      '@type': 'Product',
      name: schemaName,
      ...(schemaBrand && {
        brand: {
          '@type': 'Brand',
          name: schemaBrand,
        },
      }),
      ...(priceBound?.lower && {
        offers: {
          '@type': 'AggregateOffer',
          lowPrice: convertFromCents(priceBound?.lower),
          highPrice: convertFromCents(priceBound?.upper),
          priceCurrency: currency,
        },
      }),
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: 4.5,
        reviewCount: 44885,
      },
    };
  };

  const [schema, setSchema] = useState(() => createCatalogSchema());

  useEffect(() => {
    setSchema(createCatalogSchema());
  }, [queryResultFacets, breadcrumbs, priceBound]);

  return (
    <script
      data-cy={`catalog-schema`}
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: sanitizer(JSON.stringify(schema)) }}
    />
  );
};

export default CatalogSchema;
